// src/components/issues/AjoutMachines.tsx — SCE Issues
// Choisir d'autres machines touchées par le même défaut (Lot 14, option C) :
// à la déclaration, et depuis la fiche (« Ajouter une machine »).
//
// Deux chemins, parce que les coordinateurs ont répondu « oui » aux groupes
// sur plusieurs projets :
//   - les autres machines du MÊME projet, en bulles à toucher — le cas courant,
//     sans rien taper ;
//   - une recherche libre (N° de série, OF, PN, CVE), comme le champ
//     Équipement du formulaire — même cache mémoire, aucune requête par frappe.
// Seules les machines d'une catégorie connue (suffixe -H, -S, -SC, -B, -SD)
// sont proposées : une autre serait refusée à l'écriture.

import { useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { Plus, Search } from 'lucide-react'
import { deduireCategorieEquipement, listAllEquipements, searchEquipements } from '@/api/equipements.api'
import { cleRapprochement, type Equipement } from '@/types/issue.types'
import { useClavierEquipement } from './ClavierEquipement'

const DEBOUNCE_MS = 150
const MIN_CARACTERES = 2
/** Au-delà, les bulles du projet deviennent une liste interminable : la recherche suffit. */
const MAX_BULLES = 24

const STYLE_ETIQUETTE = {
  fontSize: 'var(--font-size-info)',
  color: 'var(--color-text-secondary)',
  display: 'block',
  margin: '0 0 6px',
} as const

function declarable(eq: Equipement): boolean {
  return deduireCategorieEquipement(eq.numeroSerie) !== null
}

export function AjoutMachines({
  pn,
  exclus,
  onAjouter,
  desactive,
}: {
  /** Projet de la machine de départ : ses autres machines sont proposées en bulles. */
  pn: string
  /** N° de série déjà pris (machine de départ, machines déjà ajoutées). */
  exclus: readonly string[]
  onAjouter: (eq: Equipement) => void
  desactive?: boolean
}): JSX.Element {
  const [projet, setProjet] = useState<Equipement[]>([])
  const [recherche, setRecherche] = useState('')
  const [suggestions, setSuggestions] = useState<Equipement[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const derniere = useRef(0)
  const champ = useRef<HTMLInputElement>(null)
  const clavier = useClavierEquipement(champ)

  const pris = useMemo(() => new Set(exclus.map(cleRapprochement)), [exclus])

  useEffect(() => {
    if (!pn) return
    let annule = false
    listAllEquipements()
      .then((tous) => {
        if (annule) return
        const cible = cleRapprochement(pn)
        setProjet(
          tous
            .filter((eq) => cleRapprochement(eq.pn) === cible && declarable(eq))
            .sort((a, b) => a.numeroSerie.localeCompare(b.numeroSerie, 'fr')),
        )
      })
      .catch(() => {
        // La recherche libre reste disponible : pas de message pour si peu.
      })
    return () => {
      annule = true
    }
  }, [pn])

  useEffect(() => {
    if (recherche.trim().length < MIN_CARACTERES) {
      setSuggestions([])
      return
    }
    const numero = ++derniere.current
    const minuteur = setTimeout(() => {
      searchEquipements(recherche.trim())
        .then((r) => {
          if (numero !== derniere.current) return
          setSuggestions(r.filter(declarable).slice(0, 8))
          setErreur(null)
        })
        .catch(() => {
          if (numero === derniere.current) setErreur('Référentiel équipements injoignable.')
        })
    }, DEBOUNCE_MS)
    return () => clearTimeout(minuteur)
  }, [recherche])

  const bulles = projet.filter((eq) => !pris.has(cleRapprochement(eq.numeroSerie)))
  const proposees = suggestions.filter((eq) => !pris.has(cleRapprochement(eq.numeroSerie)))

  function ajouter(eq: Equipement): void {
    onAjouter(eq)
    setRecherche('')
    setSuggestions([])
  }

  return (
    <div className="flex flex-col gap-3">
      {bulles.length > 0 && bulles.length <= MAX_BULLES && (
        <div>
          <span style={STYLE_ETIQUETTE}>Autres équipements du projet {pn}</span>
          <div className="flex flex-wrap gap-2">
            {bulles.map((eq) => (
              <button
                key={eq.numeroSerie}
                type="button"
                onClick={() => ajouter(eq)}
                disabled={desactive}
                title={[eq.familleEquipement, eq.of && `OF ${eq.of}`].filter(Boolean).join(' · ')}
                className="flex items-center gap-1"
                style={{
                  minHeight: 40,
                  padding: '0 12px',
                  borderRadius: 20,
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-white)',
                  color: 'var(--color-black)',
                  fontSize: 'var(--font-size-copy2)',
                }}
              >
                <Plus size={14} />
                {eq.numeroSerie}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label htmlFor="ajout-machine-recherche" style={STYLE_ETIQUETTE}>
          {bulles.length > 0 ? 'Ou un équipement d’un autre projet' : 'Rechercher l’équipement'}
        </label>
        <div
          className="flex items-center gap-2 px-3 rounded-lg"
          style={{ height: 48, border: '1px solid var(--color-border)', backgroundColor: 'var(--color-white)' }}
        >
          <Search size={16} color="var(--color-gray-70)" />
          <input
            ref={champ}
            {...clavier.proprietes}
            id="ajout-machine-recherche"
            type="text"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="N° série, OF, PN ou CVE…"
            disabled={desactive}
            className="flex-1 outline-none border-none bg-transparent"
            style={{ fontSize: 'var(--font-size-copy2)', minWidth: 0, width: '100%' }}
          />
          {clavier.bascule}
        </div>
        {erreur && <p style={{ ...STYLE_ETIQUETTE, color: 'var(--color-error)', margin: '6px 0 0' }}>{erreur}</p>}
        {proposees.length > 0 && (
          <ul
            role="listbox"
            aria-label="Équipements du référentiel"
            style={{
              margin: '6px 0 0',
              padding: 0,
              listStyle: 'none',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            {proposees.map((eq) => (
              <li key={eq.numeroSerie}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => ajouter(eq)}
                  disabled={desactive}
                  className="flex flex-col w-full text-left"
                  style={{
                    minHeight: 48,
                    padding: '8px 12px',
                    border: 'none',
                    borderTop: '1px solid var(--color-gray-10)',
                    background: 'var(--color-white)',
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600 }}>{eq.numeroSerie}</span>
                  <span style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>
                    {[eq.pn && `Projet ${eq.pn}`, eq.of && `OF ${eq.of}`, eq.familleEquipement].filter(Boolean).join(' · ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
